/**
 * Modal for inviting forensic accountants to collaborate on a report
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { useFocusTrap } from '../hooks/useFocusTrap';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface InviteAccountantModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  onInviteSent: () => void;
}

export default function InviteAccountantModal({ 
  isOpen, 
  onClose, 
  reportId,
  onInviteSent 
}: InviteAccountantModalProps) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [permissions, setPermissions] = useState<string[]>(['view', 'comment', 'edit', 'sign']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/invites`,
        {
          report_id: reportId,
          email,
          message,
          permissions
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      setInviteLink(`${window.location.origin}/accountant/accept-invite/${response.data.invite_token}`);
      
      // Log invite activity
      logActivity({
        reportId,
        actionType: 'invite',
        actionDetails: `Invited accountant ${email} to collaborate`,
        section: 'collaboration',
        metadata: { invited_email: email, permissions }
      });
      
      onInviteSent();
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setEmail('');
        setMessage('');
        setSuccess(false);
        setInviteLink('');
        onClose();
      }, 3000);
      
    } catch (err: any) {
      console.error('Failed to send invite:', err);
      setError(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (perm: string) => {
    if (permissions.includes(perm)) {
      setPermissions(permissions.filter(p => p !== perm));
    } else {
      setPermissions([...permissions, perm]);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Invite Forensic Accountant" className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <h2 className="text-xl font-bold">Invite Forensic Accountant</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Invite Sent!</h3>
              <p className="text-gray-600 mb-4">
                An invitation has been sent to <span className="font-semibold">{email}</span>
              </p>
              
              {inviteLink && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Share this link:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={copyInviteLink}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accountant Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="accountant@forensicfirm.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The accountant will receive access to this report only
                </p>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Permissions
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'view', label: 'View Report', desc: 'Can view report details and calculations' },
                    { value: 'comment', label: 'Add Comments', desc: 'Can add comments and notes' },
                    { value: 'edit', label: 'Edit Report', desc: 'Can modify report data (with version history)' },
                    { value: 'sign', label: 'E-Signature', desc: 'Can provide electronic signature' }
                  ].map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.includes(perm.value)}
                        onChange={() => togglePermission(perm.value)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{perm.label}</div>
                        <div className="text-xs text-gray-500">{perm.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Optional Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Add a personal message for the accountant..."
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || permissions.length === 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
